import { cn } from '@/lib/utils';
import type { UserExportPreset } from '@mss_creator/shared';
import { Button, Icon } from '@pms-digital/design-system';
import { useLayoutEffect, useRef, useState } from 'react';

interface ExportUserPresetRowProps {
  preset: UserExportPreset;
  /** The current options match this preset. */
  active: boolean;
  onApply: () => void;
  /** Resolves once the preset is gone; a rejection shows an inline error. */
  onDelete: () => Promise<void>;
}

const ORIENTATION_LABELS = { portrait: 'portrait', landscape: 'paysage' } as const;

/** The row's one-line summary of what the preset produces, e.g. « A3 · paysage · 13 pt ». */
function presetHint({ page }: UserExportPreset['settings']): string {
  return `${page.format.toUpperCase()} · ${ORIENTATION_LABELS[page.orientation]} · ${page.fontSizePt} pt`;
}

/**
 * One personal preset in the picker (issue #13, spec E4): a full-width ⭐ row
 * under the four built-in thumbnails. A `div` holding two sibling buttons,
 * since buttons cannot nest. The delete asks first — « Supprimer ? » then
 * « Oui » / « Non » — because it cannot be undone. Focus lands on « Non », the
 * safe answer, and returns to the trash button when the confirm closes
 * without deleting.
 */
export function ExportUserPresetRow({
  preset,
  active,
  onApply,
  onDelete,
}: ExportUserPresetRowProps) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const trashRef = useRef<HTMLButtonElement>(null);
  const keepRef = useRef<HTMLButtonElement>(null);
  /**
   * Where focus goes once the next render has swapped the row's content —
   * moved in a layout effect, so it lands in the same commit as the swap.
   */
  const focusNext = useRef<'keep' | 'trash' | null>(null);

  useLayoutEffect(() => {
    const target = focusNext.current;
    focusNext.current = null;
    if (target === 'keep') keepRef.current?.focus();
    if (target === 'trash') trashRef.current?.focus();
  });

  function openConfirm() {
    focusNext.current = 'keep';
    setConfirming(true);
  }

  function closeConfirm() {
    focusNext.current = 'trash';
    setConfirming(false);
  }

  async function confirmDelete() {
    setDeleting(true);
    setDeleteFailed(false);
    try {
      await onDelete();
    } catch {
      // Shown inline; the row stays so the user can retry.
      setDeleteFailed(true);
      closeConfirm();
    } finally {
      setDeleting(false);
    }
  }

  return (
    <li>
      <div
        className={cn(
          'flex items-center gap-1 rounded-md border-2 bg-white transition-colors duration-100',
          active ? 'border-bleu-nuit ring-1 ring-bleu-nuit' : 'border-gray-200',
        )}
      >
        {confirming ? (
          <fieldset
            aria-label={`Supprimer le preset ${preset.name} ?`}
            className="flex min-w-0 flex-1 items-center justify-between gap-2 py-1 pr-1 pl-3"
          >
            <span className="truncate font-semibold text-gray-900 text-small">Supprimer ?</span>
            <span className="flex shrink-0 gap-1">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={deleting}
                aria-label={`Oui, supprimer ${preset.name}`}
                onClick={() => void confirmDelete()}
              >
                Oui
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                ref={keepRef}
                disabled={deleting}
                onClick={closeConfirm}
              >
                Non
              </Button>
            </span>
          </fieldset>
        ) : (
          <>
            <button
              type="button"
              aria-pressed={active}
              data-preset-id={preset.id}
              onClick={onApply}
              className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-3 py-1 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
            >
              <span className="flex shrink-0 text-jaune-deep">
                <Icon name="star" size={16} filled />
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-semibold text-gray-900 text-small">
                  {preset.name}
                </span>
                <span className="truncate text-caption text-gray-500">
                  {presetHint(preset.settings)}
                </span>
              </span>
            </button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              ref={trashRef}
              icon="delete"
              aria-label={`Supprimer le preset ${preset.name}`}
              onClick={openConfirm}
            />
          </>
        )}
      </div>
      {deleteFailed && (
        <p className="mt-1 text-caption text-danger">Suppression impossible — réessayez.</p>
      )}
    </li>
  );
}
